/**
 * Data Lake API client
 */

import type { GlobalRole } from './auth'
import { apiClient } from './client'

export interface DataLabel {
  /** Total LakeDocument count under this label. */
  document_count?: number
  id: string
  name: string
  description: string | null
  level: number
  color: string | null
}

export interface LakeDocument {
  id: string
  filename: string
  s3_key: string
  file_size: number
  file_type: string | null
  status: string
  label_id: string
  created_at: string
  analysis_status: string | null
  analysis_result: any | null
  typesense_indexed: boolean
  analysis_error: string | null
  chunks_count: number | null
  phase0_status: string | null
  phase1_status: string | null
  indexing_status?: string | null
  indexing_error?: string | null
  has_parquet?: boolean | null
  parquet_path?: string | null
  converted_pdf_s3_key?: string | null
  has_pdf_preview?: boolean
  // Audio metadata — populated by the transcribe_audio step for documents
  // routed through the audio pipeline. Absent (or null) for non-audio docs.
  audio_duration_seconds?: number | null
  detected_language?: string | null
  speaker_count?: number | null
}

export interface DocumentDetailResponse {
  id: string
  filename: string
  file_size: number
  file_type: string | null
  status: string
  created_at: string
  analysis_status: string | null
  analysis_result: any | null
  analysis_error: string | null
  document_type: 'lake' | 'case' | 'lake_import'
  s3_key: string
  label_id: string | null
  typesense_indexed: boolean | null
  chunks_count: number | null
  phase0_status: string | null
  phase1_status: string | null
  indexing_status?: string | null
  indexing_error?: string | null
  has_parquet: boolean | null
  parquet_path: string | null
  converted_pdf_s3_key: string | null
  has_pdf_preview: boolean | null
  case_id: string | null
  visibility: string | null
  child_of: string | null
  copied_at: string | null
  anchor_entity_id: string | null
  anchor_entity_name: string | null
  anchor_entity_type: string | null
}

export interface LakeSearchRequest {
  query: string
  case_id?: string | null
  top_k?: number
  search_documents?: boolean
  search_chunks?: boolean
  alpha?: number
  rerank?: boolean
  page?: number
  per_page?: number
  file_type?: string | null
  label?: string | null
  document_type?: string | null
  storage_prefixes?: string[] | null
  storage_path?: string | null
  document_ids?: string[] | null
  // Date filters
  document_year?: number | null
  document_month?: number | null
  document_date_min?: number | null
  document_date_max?: number | null
  // Facets
  include_facets?: boolean
}

export type LakeMatchSource = 'path' | 'summary' | 'content'
export type LakeMatchMode = 'keyword' | 'semantic' | 'hybrid'

export interface DocumentHit {
  document_id: string
  case_id: string
  filename: string
  file_type: string | null
  document_type: string | null
  file_summary: string | null
  full_text_snippet: string | null
  score: number
  rerank_score: number | null
  vector_similarity: number | null
  text_match: number
  keyword_score: number
  match_source: Extract<LakeMatchSource, 'path' | 'summary'>
  match_mode: LakeMatchMode
  label: string | null
  storage_path: string | null
  storage_segments: string[]
  parquet_available: boolean
  created_at: number
}

export interface ChunkHit {
  chunk_id: string
  document_id: string
  case_id: string
  content_snippet: string
  content: string
  chunk_index: number | null
  chunk_type: string | null
  section: string | null
  page_number: number | null
  page_number_end: number | null
  row_index: number | null
  score: number
  rerank_score: number | null
  vector_similarity: number | null
  text_match: number
  keyword_score: number
  match_source: Extract<LakeMatchSource, 'content'>
  match_mode: LakeMatchMode
  filename: string | null
  storage_path: string | null
  storage_segments: string[]
}

export interface FacetCount {
  value: string
  count: number
}

export interface TimelineYearBucket {
  year: number
  count: number
}

export interface TimelineMonthBucket {
  month: number
  count: number
}

export interface LakeFacets {
  folders: FacetCount[]
  file_types: FacetCount[]
  document_types: FacetCount[]
  timeline_years: TimelineYearBucket[]
  timeline_months: TimelineMonthBucket[]
}

export interface LakeSearchResponse {
  query: string
  document_hits: DocumentHit[]
  chunk_hits: ChunkHit[]
  total_documents: number
  total_chunks: number
  time_ms: number
  facets: LakeFacets | null
}

export interface DocumentWithChunks {
  document: DocumentHit
  chunks: ChunkHit[]
}

export interface FederatedSearchResponse {
  query: string
  results: DocumentWithChunks[]
  total_documents: number
  total_chunks: number
  search_time_ms: number
  facets: LakeFacets | null
  page: number
  per_page: number
}

export interface FederatedSearchParams {
  query: string
  case_id?: string
  top_k_documents?: number
  top_k_chunks?: number
  chunks_per_document?: number
  alpha?: number
  file_type?: string[]
  label?: string
  document_type?: string[]
  storage_prefixes?: string[]
  document_year?: number
  document_month?: number
  document_date_min?: number
  document_date_max?: number
  include_facets?: boolean
  page?: number
  per_page?: number
}

export interface LakeStats {
  total_documents: number
  total_size_bytes: number
  by_file_type: Record<string, number>
  by_file_type_size: Record<string, number>
  by_document_type: Record<string, number>
  typesense_indexed: number
  pending_indexing: number
  processing: number
  completed: number
  failed: number
  phase0_indexed: number
  phase1_indexed: number
}

export type DocumentStatusFilter = 'pending' | 'processing' | 'indexed' | 'failed' | 'skipped'

export type FileCountByStatus = Record<DocumentStatusFilter, number>

export type StatusCounts = Record<'all' | DocumentStatusFilter, number>

export interface FolderNode {
  name: string
  path: string
  full_path: string
  file_count: number
  direct_file_count: number
  has_children: boolean
  file_count_by_status?: FileCountByStatus
}

export interface FileNode {
  id: string
  name: string
  path: string
  document: LakeDocument
}

export interface FolderTreeResponse {
  folders: FolderNode[]
  files: FileNode[]
  status_counts?: StatusCounts
}

export interface TypesenseDocRecord {
  id: string
  filename: string | null
  file_type: string | null
  document_type: string | null
  storage_path: string | null
  storage_prefixes: string[] | null
  full_text: string | null
  file_summary: string | null
  confidentiality: number | null
  created_at: number | null
  indexed_at: number | null
  imported_at: number | null // Unix timestamp — when indexed to Typesense
  document_created_at: number | null // Unix timestamp — file's own creation date
  document_year: number | null
  document_month: number | null
  has_parquet: boolean | null
  row_count: number | null
  page_count: number | null
  label: string | null
  exif_gps_latitude: number | null
  exif_gps_longitude: number | null
}

export interface TypesenseChunkRecord {
  id: string
  document_id: string
  chunk_index: number
  content: string
  chunk_type: string | null
  section: string | null
  page_number: number | null
  page_number_end: number | null
  row_index: number | null
}

export interface DocumentIndexedData {
  doc_record: TypesenseDocRecord | null
  chunks: TypesenseChunkRecord[]
  total_chunks: number
  page: number
  per_page: number
  total_pages: number
}

export interface ChunksByPageResponse {
  page_number: number
  chunks: TypesenseChunkRecord[]
}

export interface LakeUploadResponse {
  document_id: string
  filename: string
  s3_key: string
  status: string
  file_size: number
  file_type: string | null
}

// ---------------------------------------------------------------------------
// Admin types (label & grant management)
// ---------------------------------------------------------------------------

export interface UserInfo {
  id: string
  username: string
  email: string | null
  full_name: string | null
  is_active: boolean
  is_superuser: boolean
  role: GlobalRole
  created_at: string
}

export interface UserCreateRequest {
  username: string
  password: string
  full_name?: string | null
  email?: string | null
  is_superuser?: boolean
  role?: GlobalRole
}

export interface UserUpdateRequest {
  username?: string
  role?: GlobalRole
}

export interface LabelCreateRequest {
  name: string
  description?: string | null
  level?: number
  color?: string | null
}

export type LabelPermission = 'read' | 'read_write'

export interface GrantResponse {
  id: string
  user_id: string
  username: string
  label_id: string
  permission: LabelPermission
}

export interface GrantRequest {
  username: string
  permission?: LabelPermission
}

// ---------------------------------------------------------------------------
// Tabular query types (Parquet / DuckDB)
// ---------------------------------------------------------------------------

export type TabularOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'starts_with'
  | 'is_null'
  | 'is_not_null'

export interface TabularFilterCondition {
  column: string
  operator: TabularOperator
  value?: string
}

export interface TabularSort {
  column: string
  direction: 'asc' | 'desc'
}

export interface TabularColumn {
  name: string
  data_type: string // 'string' | 'integer' | 'float' | 'boolean' | 'date' | 'timestamp'
}

export interface TabularQueryRequest {
  filters?: TabularFilterCondition[]
  sort?: TabularSort
  search?: string
  search_columns?: string[]
  offset?: number
  limit?: number
  /** 0-based index into the comma-separated parquet_path list (multi-section Excel). */
  section_index?: number
}

export interface ParquetSection {
  /** 0-based position in the comma-separated parquet_path list. */
  index: number
  /** Human-readable label, e.g. "Section 1" or the file stem. */
  name: string
}

export interface TabularQueryResponse {
  columns: TabularColumn[]
  rows: (string | number | boolean | null)[][]
  total_rows: number
  offset: number
  limit: number
  /** All available Parquet sections for this document (always present). */
  sections: ParquetSection[]
}

// ─── Processing Stats types ───────────────────────────────────────────────────

export interface DocumentStatsResponse {
  id: string
  document_id: string
  char_count: number | null
  word_count: number | null
  table_count: number | null
  image_count: number | null
  section_header_count: number | null
  layout_score: number | null
  ocr_score: number | null
  mean_grade: string | null
  low_grade: string | null
  total_pages: number | null
  total_paragraphs: number | null
  total_list_items: number | null
  total_code_blocks: number | null
  total_formulas: number | null
  total_footnotes: number | null
  total_captions: number | null
  total_references: number | null
  total_hyperlinks: number | null
  total_table_rows: number | null
  avg_chars_per_page: number | null
  avg_text_coverage_pct: number | null
  parse_score: number | null
  table_score: number | null
  grade_distribution: Record<string, number> | null
  content_types: string[]
  stats: Record<string, unknown> | null
  created_at: string | null
}

export interface PageStatsSummary {
  id: string
  document_id: string
  page_number: number
  char_count: number | null
  word_count: number | null
  table_count: number | null
  image_count: number | null
  section_header_count: number | null
  mean_grade: string | null
  low_grade: string | null
}

export interface PageStatsListResponse {
  items: PageStatsSummary[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

export interface PageStatsDetail {
  id: string
  document_id: string
  page_number: number
  char_count: number | null
  word_count: number | null
  table_count: number | null
  image_count: number | null
  section_header_count: number | null
  layout_score: number | null
  ocr_score: number | null
  mean_grade: string | null
  low_grade: string | null
  paragraph_count: number | null
  title_count: number | null
  list_item_count: number | null
  code_block_count: number | null
  formula_count: number | null
  footnote_count: number | null
  caption_count: number | null
  reference_count: number | null
  hyperlink_count: number | null
  page_header_count: number | null
  page_footer_count: number | null
  table_total_rows: number | null
  table_total_cols: number | null
  text_coverage_pct: number | null
  table_coverage_pct: number | null
  image_coverage_pct: number | null
  page_width_pts: number | null
  page_height_pts: number | null
  parse_score: number | null
  table_score: number | null
  content_types: string[]
  created_at: string | null
}

export const lakeApi = {
  // Users (superuser)
  listUsers: () => apiClient.get<UserInfo[]>('/v1/users'),
  createUser: (data: UserCreateRequest) => apiClient.post<UserInfo>('/v1/users', data),
  updateUser: (userId: string, data: UserUpdateRequest) =>
    apiClient.patch<UserInfo>(`/v1/users/${userId}`, data),
  resetUserPassword: (userId: string, password: string) =>
    apiClient.put<{ message: string }>(`/v1/users/${userId}/password`, {
      password,
    }),

  // Labels
  listLabels: () => apiClient.get<DataLabel[]>('/v1/lake/labels'),

  // Label admin (superuser)
  createLabel: (data: LabelCreateRequest) => apiClient.post<DataLabel>('/v1/lake/labels', data),

  deleteLabel: (labelId: string) => apiClient.delete<void>(`/v1/lake/labels/${labelId}`),

  // Grants admin (superuser)
  listGrants: (labelId: string) =>
    apiClient.get<GrantResponse[]>(`/v1/lake/labels/${labelId}/grants`),

  createGrant: (labelId: string, data: GrantRequest) =>
    apiClient.post<GrantResponse>(`/v1/lake/labels/${labelId}/grants`, data),

  updateGrant: (
    labelId: string,
    grantId: string,
    data: { username: string; permission: LabelPermission }
  ) => apiClient.patch<GrantResponse>(`/v1/lake/labels/${labelId}/grants/${grantId}`, data),

  revokeGrant: (labelId: string, grantId: string) =>
    apiClient.delete<void>(`/v1/lake/labels/${labelId}/grants/${grantId}`),

  // Documents
  listLabelDocuments: (labelId: string) =>
    apiClient.get<LakeDocument[]>(`/v1/lake/labels/${labelId}/documents`),

  getLabelFolderTree: (labelId: string, path?: string, status?: DocumentStatusFilter) => {
    const params = new URLSearchParams()
    if (path) params.set('path', path)
    if (status) params.set('status', status)
    const query = params.toString()
    return apiClient.get<FolderTreeResponse>(
      `/v1/lake/labels/${labelId}/tree${query ? `?${query}` : ''}`
    )
  },

  getLakeDocument: (documentId: string) =>
    apiClient.get<LakeDocument>(`/v1/lake/documents/${documentId}`),

  getDocument: (documentId: string) =>
    apiClient.get<DocumentDetailResponse>(`/v1/documents/${documentId}`),

  reprocessDocument: (
    documentId: string,
    phases: string[],
    options?: { forceReanalysis?: boolean }
  ) =>
    apiClient.post<LakeDocument>(`/v1/lake/documents/${documentId}/reprocess`, {
      phases,
      force_reanalysis: options?.forceReanalysis ?? false,
    }),

  getDocumentIndexedData: (documentId: string, page = 1, perPage = 30) =>
    apiClient.get<DocumentIndexedData>(
      `/v1/lake/documents/${documentId}/indexed-data?page=${page}&per_page=${perPage}`
    ),

  getChunksByPage: (documentId: string, pageNumber: number) =>
    apiClient.get<ChunksByPageResponse>(
      `/v1/lake/documents/${documentId}/chunks-by-page?page_number=${pageNumber}`
    ),

  // Upload (label)
  uploadFile: (labelId: string, file: File, relativePath?: string, replace?: boolean) => {
    const formData = new FormData()
    formData.append('file', file)
    if (relativePath) {
      formData.append('relative_path', relativePath)
    }
    if (replace) {
      formData.append('replace', 'true')
    }
    return apiClient.post<LakeUploadResponse>(`/v1/lake/labels/${labelId}/upload`, formData)
  },

  // Pre-batch collision check: which destination-relative paths already exist
  // in the label. Powers the "replace or cancel" prompt before any upload.
  checkUploadConflicts: (labelId: string, paths: string[]) =>
    apiClient.post<{ conflicts: string[] }>(
      `/v1/lake/labels/${labelId}/documents/check-conflicts`,
      { paths }
    ),

  // Search
  search: (request: LakeSearchRequest) =>
    apiClient.post<LakeSearchResponse>('/v1/lake/search', request),

  // Federated search (hybrid vector + keyword, document-first hierarchy)
  searchFederated: (params: FederatedSearchParams) => {
    const queryParams = new URLSearchParams({ query: params.query })
    if (params.case_id) queryParams.set('case_id', params.case_id)
    if (params.top_k_documents) queryParams.set('top_k_documents', String(params.top_k_documents))
    if (params.top_k_chunks) queryParams.set('top_k_chunks', String(params.top_k_chunks))
    if (params.chunks_per_document)
      queryParams.set('chunks_per_document', String(params.chunks_per_document))
    if (params.alpha !== undefined) queryParams.set('alpha', String(params.alpha))
    if (params.file_type) {
      params.file_type.forEach(ft => queryParams.append('file_type', ft))
    }
    if (params.label) queryParams.set('label', params.label)
    if (params.document_type) {
      params.document_type.forEach(dt => queryParams.append('document_type', dt))
    }
    if (params.document_year) queryParams.set('document_year', String(params.document_year))
    if (params.document_month) queryParams.set('document_month', String(params.document_month))
    if (params.document_date_min)
      queryParams.set('document_date_min', String(params.document_date_min))
    if (params.document_date_max)
      queryParams.set('document_date_max', String(params.document_date_max))
    if (params.include_facets) queryParams.set('include_facets', 'true')
    if (params.page) queryParams.set('page', String(params.page))
    if (params.per_page) queryParams.set('per_page', String(params.per_page))
    if (params.storage_prefixes) {
      params.storage_prefixes.forEach(p => queryParams.append('storage_prefixes', p))
    }
    return apiClient.get<FederatedSearchResponse>(`/v1/lake/search/federated?${queryParams}`)
  },

  // Statistics
  getStats: () => apiClient.get<LakeStats>('/v1/lake/stats'),

  // Processing settings
  getProcessingSettings: () =>
    apiClient.get<{ ai_analysis_enabled: boolean }>('/v1/lake/settings/processing'),

  // File download (returns redirect to presigned URL)
  getFileUrl: (s3Key: string) => `/api/v1/files/${s3Key}`,

  // DOCX converted PDF preview
  getDocxPdfPreviewUrl: (documentId: string) => `/api/v1/lake/documents/${documentId}/pdf-preview`,

  // Tabular preview + query (Parquet / DuckDB)
  getTabularPreview: (
    docId: string,
    offset = 0,
    limit = 250,
    sectionIndex?: number
  ): Promise<TabularQueryResponse> => {
    const params = new URLSearchParams({
      offset: String(offset),
      limit: String(limit),
    })
    if (sectionIndex !== undefined) params.set('section_index', String(sectionIndex))
    return apiClient.get<TabularQueryResponse>(
      `/v1/lake/documents/${docId}/tabular-preview?${params}`
    )
  },

  queryTabular: (docId: string, request: TabularQueryRequest): Promise<TabularQueryResponse> =>
    apiClient.post<TabularQueryResponse>(`/v1/lake/documents/${docId}/tabular-query`, request),

  // Processing Stats
  getProcessingStats: (docId: string): Promise<DocumentStatsResponse> =>
    apiClient.get<DocumentStatsResponse>(`/v1/lake/documents/${docId}/processing-stats`),

  getProcessingStatsPages: (
    docId: string,
    page = 1,
    perPage = 25
  ): Promise<PageStatsListResponse> =>
    apiClient.get<PageStatsListResponse>(
      `/v1/lake/documents/${docId}/processing-stats/pages?page=${page}&per_page=${perPage}`
    ),

  getProcessingStatsPageDetail: (docId: string, pageNumber: number): Promise<PageStatsDetail> =>
    apiClient.get<PageStatsDetail>(
      `/v1/lake/documents/${docId}/processing-stats/pages/${pageNumber}`
    ),

  deleteDocument: (docId: string): Promise<void> =>
    apiClient.delete<void>(`/v1/lake/documents/${docId}`),

  bulkDeleteDocuments: (
    documentIds: string[]
  ): Promise<{
    deleted: number
    failed: number
    errors: Array<{ document_id: string; error: string }>
  }> => apiClient.delete(`/v1/lake/documents`, { document_ids: documentIds }),
}
